import { defineStore } from 'pinia';
import { router } from '@inertiajs/vue3';
import { timeClockService } from '@/ShopManagement/services/timeClockService.js';
import {
  DEFAULT_TIME_CLOCK_TIME_ZONE,
  entryMinutes,
} from '@/ShopManagement/timeClockFormatters.js';

/**
 * Time Clock store — the single reactive source of truth for the widget and the
 * Time Clock pages. Actions delegate all I/O to the reusable timeClockService,
 * and own the orchestration: prop hydrate vs bootstrap, per-employee/shop live
 * subscriptions, refresh-on-broadcast, and optimistic updates.
 *
 * Prefer `useTimeClockStore()` in new components; the legacy `timeClockClient`
 * facade bridges to this store for existing consumers.
 */

// Private, non-reactive bookkeeping — singletons for the app session, matching
// the previous adapter (these are internal and never rendered).
let hydrated = false;
let bootstrapping = false;
let recordsPageActive = false;
let personalChannelName = null;
let shopChannelName = null;
let liveRefreshTimer = null;

const initialState = () => ({
  // False until the first server state (prop hydrate or bootstrap) has been
  // applied, so pages can show a loading state instead of default settings.
  loaded: false,
  capabilities: { canManage: false, canReview: false, canConfigure: false, canExport: false },
  currentEmployeeId: null,
  shops: [],
  employees: [],
  breakTypes: [],
  status: {
    state: 'clocked_out',
    clockEntryId: null,
    clockedInAt: null,
    activeBreak: null,
    todayWorkedMinutes: 0,
    activeServiceClock: null,
    shopTimeZone: DEFAULT_TIME_CLOCK_TIME_ZONE,
  },
  employeeStatuses: {},
  records: [],
  reviews: [],
  exporters: [],
  settings: {
    // Time Clock is on by default; the server sends the authoritative value on hydrate.
    enabled: true,
    // New shops default to a two-week (biweekly) pay cycle; overwritten on hydrate.
    payPeriod: { cadence: 'biweekly' },
    pdf: { acknowledgmentStatement: '' },
    location: {
      enabled: false,
      allowedDistance: 500,
      distanceUnit: 'feet',
      allowClockInExceptions: true,
    },
    breakTypes: [],
  },
});

export const useTimeClockStore = defineStore('timeClock', {
  state: initialState,

  getters: {
    enabledBreakTypes: (state) => state.breakTypes.filter((type) => type.enabled),
    timeClockEnabled: (state) => state.settings.enabled !== false,
    // The shop's Time Clock is on and the employee has it enabled in their
    // settings. Someone already clocked in keeps access so they can clock out.
    canUseClock: (state) =>
      state.settings.enabled !== false &&
      (Boolean(state.capabilities?.canClock) || state.status.state !== 'clocked_out'),
  },

  actions: {
    applyState(payload) {
      if (!payload) return;
      Object.assign(this.$state, payload);
      hydrated = true;
      this.loaded = true;
      this.ensureLiveSubscription();
    },

    hydrateFromProps(payload = null) {
      // Prefer the payload the calling component hands us from its own
      // `$page.props` (always reliable); fall back to the router page only if none
      // was provided.
      const data = payload ?? router.page?.props?.timeClock;
      if (data) {
        this.applyState(data);

        return true;
      }

      return false;
    },

    async bootstrap() {
      if (bootstrapping || !timeClockService.currentShopId()) return;
      bootstrapping = true;
      try {
        this.applyState(await timeClockService.bootstrap());
      } catch (error) {
        // The widget stays in its default clocked-out view if bootstrap fails.
      } finally {
        bootstrapping = false;
        // Release the loading state even on failure, so the UI falls back to
        // defaults instead of spinning indefinitely.
        this.loaded = true;
      }
    },

    ensureCurrentUser(user = null, payload = null) {
      if (this.hydrateFromProps(payload)) return;
      if (!hydrated) this.bootstrap();
    },

    /**
     * Pull authoritative state back from the server after a broadcast. On the
     * full Time Clock page reload the history-bearing `timeClock` prop; anywhere
     * else the lightweight bootstrap (status + today) is enough.
     */
    refreshFromServer() {
      if (recordsPageActive) {
        router.reload({
          only: ['timeClock'],
          onSuccess: (page) => this.hydrateFromProps(page?.props?.timeClock),
        });
      } else {
        this.bootstrap();
      }
    },

    scheduleRefresh() {
      // Debounce: a single action can emit a couple of saves in quick succession,
      // and both the personal and shop channels can fire for the same action.
      if (liveRefreshTimer) window.clearTimeout(liveRefreshTimer);
      liveRefreshTimer = window.setTimeout(() => this.refreshFromServer(), 150);
    },

    /**
     * Subscribe the current user to their personal Time Clock channel so the
     * top-bar widget refreshes only when their own clock changes. Idempotent;
     * re-subscribes if the shop or user changes.
     */
    ensureLiveSubscription() {
      const id = timeClockService.currentShopId();
      const userId = this.currentEmployeeId;
      if (!id || !userId || typeof window === 'undefined' || !window.Echo) return;

      const name = `shop-management.time-clock.${id}.user.${userId}`;
      if (personalChannelName === name) return;
      if (personalChannelName) {
        timeClockService.leave(personalChannelName);
      }
      personalChannelName = name;
      timeClockService.subscribe(name, () => this.scheduleRefresh());
    },

    /**
     * Subscribe/unsubscribe the shop-wide channel — used only while a page that
     * shows many employees' clocks (My Time / Time Records / Review) is open, so
     * it refreshes on anyone's change.
     */
    setShopSubscription(active) {
      if (typeof window === 'undefined' || !window.Echo) return;
      const id = timeClockService.currentShopId();
      const name = active && id ? `shop-management.time-clock.${id}` : null;

      if (shopChannelName === name) return;
      if (shopChannelName) {
        timeClockService.leave(shopChannelName);
      }
      shopChannelName = name;
      if (name) {
        timeClockService.subscribe(name, () => this.scheduleRefresh());
      }
    },

    setRecordsPageActive(active) {
      recordsPageActive = Boolean(active);
      this.setShopSubscription(recordsPageActive);
    },

    upsertRecord(record) {
      if (!record) return;
      const index = this.records.findIndex((item) => item.id === record.id);
      if (index === -1) this.records.unshift(record);
      else this.records.splice(index, 1, record);
    },

    upsertReview(review) {
      if (!review) return;
      const index = this.reviews.findIndex((item) => item.id === review.id);
      if (index === -1) this.reviews.unshift(review);
      else this.reviews.splice(index, 1, review);
    },

    applyClockResult(data) {
      if (!data) return { status: this.status, stoppedServiceClock: false };
      if (data.status) this.status = data.status;
      if (data.record) this.upsertRecord(data.record);

      return {
        status: data.status ?? this.status,
        stoppedServiceClock: Boolean(data.stoppedServiceClock),
      };
    },

    async clockIn(payload = {}) {
      return this.applyClockResult(await timeClockService.clockIn(payload));
    },

    async clockOut(payload = {}) {
      return this.applyClockResult(await timeClockService.clockOut(payload));
    },

    async startBreak(breakTypeId) {
      return this.applyClockResult(await timeClockService.startBreak(breakTypeId));
    },

    async endBreak() {
      return this.applyClockResult(await timeClockService.endBreak());
    },

    async saveEntry(payload) {
      const data = await timeClockService.saveEntry(payload);
      this.upsertRecord(data?.record);

      return data?.record;
    },

    async requestCorrection(payload) {
      const data = await timeClockService.requestCorrection(payload);
      this.upsertReview(data?.review);
    },

    async requestMissingClock(payload) {
      const data = await timeClockService.requestMissingClock(payload);
      this.upsertReview(data?.review);

      return data?.review;
    },

    async resolveReview(reviewId, status, proposed = null) {
      const data = await timeClockService.resolveReview(reviewId, status, proposed);
      this.upsertReview(data?.review);
      this.upsertRecord(data?.record);

      return data?.review;
    },

    employeeStatus(employeeId) {
      if (employeeId === this.currentEmployeeId) return this.status;

      return (
        this.employeeStatuses[employeeId] || {
          state: 'clocked_out',
          clockEntryId: null,
          clockedInAt: null,
          activeBreak: null,
        }
      );
    },

    async managerSetEmployeeClock(payload) {
      const data = await timeClockService.managerSetEmployeeClock(payload);
      // The day's record carries the new or closed clock entry, so the Time
      // Records totals reflect it without waiting for a live refresh.
      if (data?.record) this.upsertRecord(data.record);
      if (data?.employeeStatuses) {
        Object.entries(data.employeeStatuses).forEach(([id, status]) => {
          this.employeeStatuses[id] = status;
        });
      }
      if (Number(payload.employeeId) === Number(this.currentEmployeeId) && data?.status) {
        this.status = data.status;
      }

      return { status: data?.status, stoppedServiceClock: Boolean(data?.stoppedServiceClock) };
    },

    async saveSettings(settings) {
      const data = await timeClockService.saveSettings(settings);
      if (data?.settings) {
        this.settings = data.settings;
        this.breakTypes = data.settings.breakTypes || this.breakTypes;
      }

      return data?.settings;
    },

    refreshTodayWorkedMinutes() {
      const record = this.records.find((item) =>
        item.entries?.some((entry) => entry.id === this.status.clockEntryId),
      );
      if (!record) return;
      // Time on any break, paid or not, is not time worked.
      const onBreak = record.entries
        .filter((entry) => entry.type === 'break')
        .reduce((sum, entry) => sum + entryMinutes(entry), 0);
      const clock = record.entries
        .filter((entry) => entry.type === 'clock')
        .reduce((sum, entry) => sum + entryMinutes(entry), 0);
      this.status.todayWorkedMinutes = Math.max(0, clock - onBreak);
    },
  },
});
